// Small bottom-centre confirmation toast (e.g. "Link copied").
// For a wide top-anchored notification with a longer message, use `./banner.ts`.

export type ToastVariant = 'neutral' | 'critical';

export type ShowToastOptions = {
  variant?: ToastVariant;
  dismissMs?: number;
};

const DEFAULT_DISMISS_MS = 3000;

function toastIconMarkup(variant: ToastVariant): string {
  if (variant === 'critical') {
    return `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
      <path d="M8 5v3.5M8 11h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`;
  }

  return `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
    <path d="M5.5 8.25l1.75 1.75 3.25-3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
}

function createToastElement(messageHtml: string, variant: ToastVariant): HTMLElement {
  const toast = document.createElement('div');
  const classes = ['toast'];
  if (variant === 'critical') classes.push('toast--critical');
  toast.className = classes.join(' ');
  toast.setAttribute('role', variant === 'critical' ? 'alert' : 'status');
  toast.setAttribute('aria-live', variant === 'critical' ? 'assertive' : 'polite');

  toast.innerHTML = `
    <span class="toast__body">
      <span class="toast__icon" aria-hidden="true">${toastIconMarkup(variant)}</span>
      <span class="toast__message">${messageHtml}</span>
    </span>
    <button type="button" class="toast__close" aria-label="Dismiss notification">
      <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
    </button>
  `;

  return toast;
}

/**
 * Shows a single transient toast. A second call replaces any toast already
 * showing (single-instance). `messageHtml` is trusted markup — callers MUST
 * escape any interpolated user/CMS content themselves.
 */
export function showToast(messageHtml: string, options: ShowToastOptions = {}): void {
  const { variant = 'neutral', dismissMs = DEFAULT_DISMISS_MS } = options;

  const existing = document.querySelector('.toast');
  if (existing) existing.remove();

  const toast = createToastElement(messageHtml, variant);
  document.body.appendChild(toast);

  let dismissTimeout: number | null = null;

  const dismiss = (): void => {
    toast.classList.add('toast--hiding');
    toast.addEventListener('animationend', () => toast.remove(), { once: true });
    // remove even if the hide animation never fires (reduced motion, missing CSS)
    window.setTimeout(() => toast.remove(), 400);
    if (dismissTimeout !== null) window.clearTimeout(dismissTimeout);
  };

  const closeBtn = toast.querySelector<HTMLButtonElement>('.toast__close');
  if (closeBtn) {
    closeBtn.addEventListener('click', dismiss);
  }

  requestAnimationFrame(() => {
    toast.classList.add('toast--visible');
  });

  dismissTimeout = window.setTimeout(dismiss, dismissMs);
}
